import { TableCell } from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import * as customColors from "../../../../theme/customColors";
import {
  buildHaklaiFieldShellStyles,
  buildHaklaiRowFlashStyles,
  buildHaklaiSectionChromeStyles,
  HAKLAI_FIELD_ERROR_SLOT_WIDTH,
  HAKLAI_FIELD_CURRENCY_SLOT_WIDTH,
  HAKLAI_FIELD_SLOT_GAP,
  haklaiNameColumnWidth,
  haklaiAddPillStyles,
  haklaiColumnBorder,
  haklaiLockableSectionStyles,
  haklaiSectionFrameBorder,
  haklaiStickyHeaderStyles,
  haklaiTableWrapperBaseStyles
} from "../shared/table/haklaiTableShared";
import { HAKLAI_PROPERTY_PART_LABELS } from "./haklaiPropertyPartsTexts";

export const MONEY_FIELD_WIDTH = 100;
export const STANDARD_VALUE_COLUMN_WIDTH = 136;
export const READ_ONLY_VALUE_COLUMN_WIDTH = 120;
export const REMAINING_COLUMN_WIDTH = READ_ONLY_VALUE_COLUMN_WIDTH;
export const PROPERTY_TYPE_COLUMN_WIDTH = 76;
export const DEPRECIATION_COLUMN_WIDTH = 136;
export const ACTION_COLUMN_WIDTH = 32;
export const VALIDATION_ERROR_COLOR = customColors.red;

// ── Local design tokens ───────────────────────────────────────────────────
// The haklai section needs a handful of off-gray shades that the project
// theme does not yet expose. We keep them co-located here, documented, and
// named by *role* (not hue) so the intent stays visible at every call site
// and so a future theme pass can swap them out in one place.
const palette = {
  // Neutral greys used for disabled / read-only / muted content.
  mutedText: "#8a939d",                   // inactive helper text, placeholders
  subtleText: "#a7afb7",                  // even lighter muted content
  // Soft surfaces for read-only and non-relevant cells.
  readOnlySurface: "#f7f9fb",
  groupBandSurface: "#f0f4f8",            // blue-grey band behind sub-section labels
  optionalFieldSurface: "#fbfcfd",        // trailing "draft" optional row
  // Hover / selected surfaces inside the add-row menu.
  menuSelectedHover: "#ced6da",
  // Error palette (focus ring, flash keyframes, text)
  // now lives in `shared/table/haklaiTableShared.js` → `HAKLAI_ERROR_PALETTE`,
  // so the expenses table shares the exact same tokens.
  // Thin rule used for read-only field outlines.
  readOnlyBorder: "rgba(120, 144, 156, 0.28)",
  // Group-frame accents (blue outline that wraps a sub-section body).
  groupFrameBorder: "2px solid rgba(51, 107, 136, 0.72)"
};
export const SELECT_NAME_PLACEHOLDER = HAKLAI_PROPERTY_PART_LABELS.selectAssetType;
export const SALE_VALUE_PLACEHOLDER = HAKLAI_PROPERTY_PART_LABELS.saleValuePlaceholder;
export const BUILT_VALUE_PLACEHOLDER = HAKLAI_PROPERTY_PART_LABELS.builtValuePlaceholder;
const columnGroupFrameBorder = palette.groupFrameBorder;
const TABLE_SCROLL_MEDIA_QUERY = "@media (max-width: 1280px)";
// Dimensions come from the shared tokens so the two tables can never drift.
// Re-exported here for any downstream reader searching by the original name.
const FIELD_SLOT_GAP = HAKLAI_FIELD_SLOT_GAP;
const FIELD_ERROR_SLOT_WIDTH = HAKLAI_FIELD_ERROR_SLOT_WIDTH;
const FIELD_CURRENCY_SLOT_WIDTH = HAKLAI_FIELD_CURRENCY_SLOT_WIDTH;
export const NAME_FIELD_WIDTH = haklaiNameColumnWidth - FIELD_ERROR_SLOT_WIDTH - FIELD_SLOT_GAP - 2;
const NAME_FIELD_SHELL_WIDTH = NAME_FIELD_WIDTH + FIELD_ERROR_SLOT_WIDTH + FIELD_SLOT_GAP;
const MONEY_FIELD_SHELL_WIDTH = MONEY_FIELD_WIDTH + FIELD_ERROR_SLOT_WIDTH + FIELD_CURRENCY_SLOT_WIDTH + (FIELD_SLOT_GAP * 2);
const READ_ONLY_MONEY_FIELD_SHELL_WIDTH = MONEY_FIELD_WIDTH + FIELD_CURRENCY_SLOT_WIDTH + FIELD_SLOT_GAP;
const NAME_FIELD_STACK_GAP = 3;
const NAME_FIELD_STACK_MIN_HEIGHT = 34;

// Base cell for the blue group-header row. Every call site sets its own
// background via a class (lightBlue for the title/action cells, a spacer
// class for the empty cells), so no default background here.
//
// RTL note: `textAlign: "start"` + `paddingRight` intentionally mix logical
// and physical properties — the padding override targets MUI's own physical
// `paddingRight` on `.MuiTableCell-sizeSmall:last-child` and must stay in
// sync with the rule it is overriding.
export const StyledGroupCell = withStyles({
  root: {
    padding: "4px 6px",
    "&.MuiTableCell-sizeSmall:last-child": {
      paddingRight: 6
    },
    borderBottom: "none",
    textAlign: "start"
  }
})(TableCell);

export const useHaklaiPropertyPartsStyles = makeStyles((theme) => ({
  // `error={true}` on the TextField adds `.Mui-error` to the outlined input
  // root, which is what the nested selector below hooks into. We override
  // MUI's default 1px/2px (normal/focused) borders with a single 1.5px so
  // the error state stays visually stable across hover & focus without the
  // input "jumping" to a 2px focused border.
  numberInput: {
    width: MONEY_FIELD_WIDTH,
    "& .MuiOutlinedInput-root.Mui-error": {
      "& fieldset": {
        borderColor: VALIDATION_ERROR_COLOR,
        borderWidth: 1.5
      },
      "&:hover fieldset": {
        borderColor: VALIDATION_ERROR_COLOR
      },
      "&.Mui-focused fieldset": {
        borderColor: VALIDATION_ERROR_COLOR,
        borderWidth: 1.5
      }
    },
    "& input": {
      // Numeric right-alignment is an accounting convention that stays
      // physical even in RTL (money columns line up their ones digit on
      // the right edge regardless of script direction).
      textAlign: "right",
      padding: "5px 6px",
      fontSize: "0.85rem"
    },
    "& .MuiFormHelperText-root.Mui-error, & .MuiSvgIcon-root.Mui-error": {
      color: VALIDATION_ERROR_COLOR
    }
  },
  readOnlyField: {
    width: MONEY_FIELD_WIDTH,
    "& .MuiOutlinedInput-root": {
      backgroundColor: palette.readOnlySurface,
      color: customColors.darkGray,
      "& fieldset": {
        borderColor: palette.readOnlyBorder,
        borderWidth: 1
      },
      "&:hover fieldset": {
        borderColor: palette.readOnlyBorder
      },
      "&.Mui-focused fieldset": {
        borderColor: palette.readOnlyBorder,
        borderWidth: 1
      }
    },
    "& .MuiOutlinedInput-input": {
      // See numberInput: numeric right-alignment stays physical.
      textAlign: "right",
      padding: "5px 6px",
      fontSize: "0.85rem",
      color: customColors.darkGray,
      cursor: "default"
    }
  },
  readOnlyFieldWide: {
    width: MONEY_FIELD_WIDTH,
    "& .MuiOutlinedInput-root": {
      backgroundColor: palette.readOnlySurface,
      color: customColors.darkGray,
      "& fieldset": {
        borderColor: palette.readOnlyBorder,
        borderWidth: 1
      },
      "&:hover fieldset": {
        borderColor: palette.readOnlyBorder
      },
      "&.Mui-focused fieldset": {
        borderColor: palette.readOnlyBorder,
        borderWidth: 1
      }
    },
    "& .MuiOutlinedInput-input": {
      textAlign: "center",
      padding: "5px 6px",
      fontSize: "0.85rem",
      color: customColors.darkGray,
      cursor: "default"
    }
  },
  percentCell: {
    textAlign: "center"
  },
  ...haklaiLockableSectionStyles,
  nonRelevantCell: {
    backgroundColor: palette.readOnlySurface
  },
  nonRelevantValue: {
    color: palette.subtleText,
    fontSize: "0.86rem",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: 1.25,
    textAlign: "center"
  },
  zabanDetailsStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
    margin: "0 auto"
  },
  pre1997QuestionWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    width: READ_ONLY_MONEY_FIELD_SHELL_WIDTH,
    maxWidth: "100%",
    margin: "0 auto",
    color: customColors.darkGray
  },
  pre1997QuestionLabel: {
    fontSize: "0.71rem",
    fontWeight: 600,
    lineHeight: 1.15,
    textAlign: "center",
    maxWidth: 112
  },
  pre1997Checkbox: {
    padding: 2
  },
  ...buildHaklaiFieldShellStyles(),
  moneyFieldShell: {
    width: MONEY_FIELD_SHELL_WIDTH,
    maxWidth: "100%"
  },
  readOnlyMoneyFieldShell: {
    width: READ_ONLY_MONEY_FIELD_SHELL_WIDTH,
    maxWidth: "100%",
    "& $fieldShellStatusSlot": {
      display: "none"
    }
  },
  nameFieldShell: {
    width: NAME_FIELD_SHELL_WIDTH,
    maxWidth: "100%"
  },
  depreciationFieldContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    minHeight: 34
  },
  rentalDepreciationFieldShell: {
    width: DEPRECIATION_COLUMN_WIDTH,
    maxWidth: "100%"
  },
  rentalDepreciationControl: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 0,
    direction: "rtl",
    minHeight: 34,
    whiteSpace: "nowrap"
  },
  rentalDepreciationActionRow: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    margin: 0,
    direction: "rtl"
  },
  rentalDepreciationRadioGroup: {
    display: "inline-flex",
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "nowrap",
    gap: 1
  },
  rentalDepreciationRadioOption: {
    margin: 0,
    "& .MuiFormControlLabel-label": {
      color: customColors.darkGray,
      fontSize: "0.78rem",
      fontWeight: 700,
      lineHeight: 1
    }
  },
  rentalDepreciationRadio: {
    padding: 1
  },
  rentalDepreciationEditButton: {
    width: 26,
    height: 26,
    padding: 3,
    borderRadius: 6,
    color: customColors.sessionBlue,
    border: "1px solid transparent",
    transition: theme.transitions.create(["background-color", "border-color", "color"], {
      duration: theme.transitions.duration.shortest
    }),
    "&:hover": {
      backgroundColor: "#edf6fa",
      borderColor: "rgba(51, 107, 136, 0.32)"
    }
  },
  rentalDepreciationEditButtonActive: {
    backgroundColor: "#f7fbfd",
    borderColor: "rgba(51, 107, 136, 0.28)"
  },
  rentalDepreciationEditButtonWarning: {
    borderColor: VALIDATION_ERROR_COLOR,
    "&:hover": {
      borderColor: VALIDATION_ERROR_COLOR
    }
  },
  rentalDepreciationEditButtonDisabled: {
    color: "#a7b0b8",
    cursor: "default",
    opacity: 0.55,
    borderColor: "transparent",
    backgroundColor: "transparent",
    "&:hover": {
      color: "#a7b0b8",
      borderColor: "transparent",
      backgroundColor: "transparent"
    }
  },
  fieldShellPlaceholder: {
    width: MONEY_FIELD_WIDTH,
    minHeight: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  headerLabelText: {
    fontFamily: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    letterSpacing: "inherit",
    lineHeight: 1.2,
    whiteSpace: "normal",
    direction: "rtl",
    unicodeBidi: "isolate"
  },
  // Override pairs these to the shared `HaklaiStyledHeaderCell` emitters in
  // `haklaiTableShared.js`, which are now logical (`borderInlineStart` /
  // `borderInlineEnd`). If the shared sheet is ever flipped back to physical
  // borders, these override property names must flip to match — the two
  // sheets stay locked together.
  propertyTableHeaderCell: {
    borderInlineEnd: "none !important"
  },
  propertyTableGroupBoundaryHeaderCell: {
    borderInlineStart: `${columnGroupFrameBorder} !important`
  },
  propertyTableGroupEndHeaderCell: {
    borderInlineEnd: `${columnGroupFrameBorder} !important`
  },
  propertyTableTrailingHeaderCell: {
    boxShadow: `inset 1px 0 0 ${customColors.lightGray}`
  },
  propertyLeadingColumnTopRule: {
    position: "relative",
    overflow: "hidden",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      borderTop: columnGroupFrameBorder,
      zIndex: 2
    }
  },
  columnGroupCell: {
    position: "relative",
    borderInlineStart: "none !important",
    borderInlineEnd: "none !important",
    boxShadow: "none !important",
    "--column-group-top": "0 solid transparent",
    "--column-group-bottom": "0 solid transparent",
    "--column-group-start": "0 solid transparent",
    "--column-group-end": "0 solid transparent",
    "--column-group-inner-start": "0 solid transparent",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      borderTop: "var(--column-group-top)",
      borderBottom: "var(--column-group-bottom)",
      borderInlineStart: "var(--column-group-start)",
      borderInlineEnd: "var(--column-group-end)"
    },
    "&::before": {
      content: '""',
      position: "absolute",
      insetBlock: 0,
      insetInlineStart: 0,
      pointerEvents: "none",
      borderInlineStart: "var(--column-group-inner-start)"
    }
  },
  columnGroupTop: {
    "--column-group-top": columnGroupFrameBorder
  },
  columnGroupBottom: {
    "--column-group-bottom": columnGroupFrameBorder
  },
  columnGroupStart: {
    "--column-group-start": columnGroupFrameBorder
  },
  columnGroupInnerStart: {
    "--column-group-inner-start": haklaiColumnBorder
  },
  columnGroupEnd: {
    "--column-group-end": columnGroupFrameBorder
  },
  groupFrameTrailingActionCell: {
    borderInlineStart: "none !important"
  },
  tableWrapper: {
    ...haklaiTableWrapperBaseStyles,
    position: "relative",
    zIndex: 0,
    display: "inline-block",
    width: "auto",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
    marginTop: 6,
    backgroundColor: customColors.white,
    [TABLE_SCROLL_MEDIA_QUERY]: {
      display: "block",
      width: "100%",
      overflowX: "auto",
      overflowY: "hidden",
      WebkitOverflowScrolling: "touch"
    },
    [theme.breakpoints.down("xs")]: {
      "--haklai-nav-height": "56px"
    }
  },
  tableWrapperScrollable: {
    display: "block",
    width: "100%",
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch"
  },
  tableFrameAlign: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    minWidth: 0,
    maxWidth: "100%"
  },
  stickyHeader: {
    ...haklaiStickyHeaderStyles,
    zIndex: 2,
    [TABLE_SCROLL_MEDIA_QUERY]: {
      position: "static"
    }
  },
  stickyHeaderDisabled: {
    position: "static"
  },
  subSectionCell: {
    backgroundColor: palette.groupBandSurface,
    textAlign: "center",
    verticalAlign: "middle",
    fontWeight: 700,
    color: customColors.darkGray,
    padding: "5px 4px"
  },
  subSectionCellInner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    minHeight: 28
  },
  subSectionCellText: {
    fontWeight: 700,
    fontSize: "0.82rem"
  },
  sectionFrameBody: {
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      inset: 0,
      border: haklaiSectionFrameBorder,
      zIndex: 1,
      pointerEvents: "none"
    }
  },
  sectionFrameBodyNoTop: {
    "&::before": {
      borderTop: "none"
    }
  },
  groupHeaderLeadTitle: {
    fontWeight: 700,
    fontSize: "0.95rem",
    lineHeight: 1.2,
    color: customColors.white,
    textAlign: "center",
    direction: "rtl",
    unicodeBidi: "isolate"
  },
  groupHeaderSpacer: {
    width: "100%",
    minHeight: 20
  },
  emptyStateCell: {
    backgroundColor: palette.readOnlySurface,
    padding: "12px 10px",
    textAlign: "center"
  },
  emptyStateText: {
    color: palette.mutedText,
    fontSize: "0.84rem",
    fontWeight: 600,
    lineHeight: 1.35
  },
  groupHeaderSpacerCell: {
    backgroundColor: customColors.lightBlue,
    color: customColors.white,
    fontWeight: "bold",
    fontSize: "0.9rem"
  },
  // Lead cell of the group-header row — carries the section title
  // ("חלקה א׳" / "חלקה ב+ג") on a lightBlue background.
  groupHeadLeadCell: {
    width: PROPERTY_TYPE_COLUMN_WIDTH,
    minWidth: PROPERTY_TYPE_COLUMN_WIDTH,
    maxWidth: PROPERTY_TYPE_COLUMN_WIDTH,
    backgroundColor: customColors.lightBlue,
    color: customColors.white,
    fontWeight: "bold",
    fontSize: "0.9rem",
    textAlign: "center"
  },
  // Trailing cell of the group-header row — hosts the "+" button that
  // opens the add-row menu for the section.
  groupHeadActionCell: {
    width: ACTION_COLUMN_WIDTH,
    minWidth: ACTION_COLUMN_WIDTH,
    maxWidth: ACTION_COLUMN_WIDTH,
    padding: "4px 0",
    backgroundColor: customColors.lightBlue,
    color: customColors.white,
    fontWeight: "bold",
    fontSize: "0.9rem"
  },
  groupHeaderActionWrap: {
    width: "100%",
    minHeight: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  sectionAddMenuItem: {
    alignItems: "flex-start",
    justifyContent: "flex-end",
    paddingTop: 7,
    paddingBottom: 7,
    "&.Mui-selected": {
      backgroundColor: customColors.greyAndBlue
    },
    "&.Mui-selected:hover": {
      backgroundColor: palette.menuSelectedHover
    },
    "&.Mui-disabled": {
      opacity: 1,
      color: theme.palette.text.secondary
    }
  },
  sectionAddMenuItemContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 2,
    marginInlineStart: "auto",
    textAlign: "start"
  },
  sectionAddMenuItemTitle: {
    fontSize: "0.9rem",
    fontWeight: 500,
    lineHeight: 1.2,
    color: "inherit"
  },
  ...haklaiAddPillStyles,
  // Error-state styling matches `numberInput`: `error={true}` on the
  // TextField adds `.Mui-error` to the outlined input root; we override
  // MUI's default 1px/2px (normal/focused) borders with a single 1.5px so
  // the error outline stays visually stable across hover & focus.
  commercialNameInput: {
    width: NAME_FIELD_WIDTH,
    maxWidth: "100%",
    margin: "0 auto",
    "& .MuiOutlinedInput-root": {
      backgroundColor: customColors.white
    },
    "& .MuiOutlinedInput-root.Mui-error": {
      "& fieldset": {
        borderColor: VALIDATION_ERROR_COLOR,
        borderWidth: 1.5
      },
      "&:hover fieldset": {
        borderColor: VALIDATION_ERROR_COLOR
      },
      "&.Mui-focused fieldset": {
        borderColor: VALIDATION_ERROR_COLOR,
        borderWidth: 1.5
      }
    },
    "& .MuiOutlinedInput-input": {
      fontSize: "0.875rem",
      padding: "5px 6px",
      textAlign: "start"
    },
    "& .MuiSelect-select": {
      fontSize: "0.875rem",
      fontWeight: 500,
      padding: "5px 28px 5px 6px",
      textAlign: "start",
      minHeight: "unset"
    }
  },
  // Applied to the name / sale-value / built-value inputs while the row is
  // the trailing row and no user-facing field has data yet (see
  // `isVisuallyDrafting` in `getPropertyPartRowValidationState`). The
  // transition softens the pale-to-normal handoff when the user commits
  // the first character of real data, avoiding a hard "flash" even though
  // the flip is scoped to the input they're typing in.
  optionalTrailingField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: palette.optionalFieldSurface,
      transition: "background-color 180ms ease"
    },
    "& .MuiOutlinedInput-input": {
      color: palette.mutedText,
      transition: "color 180ms ease"
    },
    "& .MuiSelect-select": {
      color: palette.mutedText,
      transition: "color 180ms ease"
    }
  },
  readOnlyNameInput: {
    "& .MuiInputBase-root": {
      display: "flex",
      alignItems: "center",
      minHeight: 29,
      direction: "rtl"
    },
    "& input": {
      fontSize: "0.875rem",
      padding: "4px 6px",
      fontWeight: 500,
      cursor: "default",
      color: customColors.darkGray,
      minWidth: 0,
      textOverflow: "ellipsis"
    },
    width: NAME_FIELD_WIDTH,
    maxWidth: "100%",
    borderRadius: 4,
    backgroundColor: palette.readOnlySurface,
    border: `1px solid ${palette.readOnlyBorder}`,
    margin: "0 auto"
  },
  readOnlyNameInputError: {
    borderColor: VALIDATION_ERROR_COLOR
  },
  readOnlyNameInputWithBadge: {
    "& input": {
      paddingLeft: 1,
      fontSize: "0.84rem"
    }
  },
  readOnlyNameBadgeAdornment: {
    flex: "0 0 auto",
    marginLeft: 2,
    marginRight: 0,
    maxWidth: "none",
    pointerEvents: "auto",
    direction: "rtl"
  },
  readOnlyNameBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1px 4px",
    maxWidth: "none",
    margin: 0,
    borderRadius: 4,
    border: "1px solid rgba(51, 107, 136, 0.28)",
    backgroundColor: "rgba(51, 107, 136, 0.07)",
    color: customColors.sessionBlue,
    fontSize: "0.62rem",
    fontWeight: 800,
    whiteSpace: "nowrap",
    lineHeight: 1.25
  },
  nameFieldStack: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: NAME_FIELD_STACK_GAP,
    margin: "0 auto",
    minHeight: NAME_FIELD_STACK_MIN_HEIGHT,
    width: NAME_FIELD_SHELL_WIDTH,
    maxWidth: "100%"
  },
  nameFieldStackCentered: {
    justifyContent: "center"
  },
  ...buildHaklaiSectionChromeStyles(theme),

  // Row flash styles live in the shared factory so the expenses sheet spreads
  // the exact same keys. The field error indicator
  // (`fieldErrorIcon`, `fieldErrorIconHidden`, `visuallyHidden`) already
  // comes from `buildHaklaiFieldShellStyles()` above.
  ...buildHaklaiRowFlashStyles()
}));
